export function cleanString(str: string | undefined) {
  return str ? str.replace(/\s+/g, ' ').trim() : str;
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} str - The input string to capitalize.
 * @returns {string} The input string with the first letter capitalized.
 * If the input is empty or nullish, returns the original input unchanged.
 *
 * @example
 * // Returns "Hello"
 * capitalize("hello");
 *
 * @example
 * // Returns "Test string"
 * capitalize("test string");
 */
export const capitalize = (str: string) => {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Transforms a string to capital/title case.
 * @example
 * toCapitalCase('list')       -> 'List'
 * toCapitalCase('text area')  -> 'Text Area'
 * @param str The input string to transform
 */
export function toCapitalCase(str: string): string {
  if (!str) {
    return str;
  }

  return str
    .split(' ')
    .map((word) => {
      if (word.length === 0) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Returns an array with only the first occurrence of each unique value for a specified property
 * @param array The array of objects to filter
 * @param property The property name to check for uniqueness
 * @returns A filtered array with only the first occurrence of each unique property value
 */
export function getUniqueByProperty<T>(array: T[], property: keyof T): T[] {
  const seenValues = new Set<any>();
  const uniqueItems: T[] = [];

  for (const item of array) {
    const value = item[property];

    // Make sure the property value is a string or can be converted to one
    const strValue = String(value);

    if (!seenValues.has(strValue)) {
      seenValues.add(strValue);
      uniqueItems.push(item);
    }
  }

  return uniqueItems;
}